'use client';

import { c as _c } from "react/compiler-runtime";
import { Heading, Icon, IconButton, LayoutCluster, LayoutStack, WaveyWrapper } from 'design-system/client';
import { useTranslations, useLocale } from 'next-intl';
import analytics, { DonationPaginationClickedData } from 'product-analytics';
import { useCallback, useEffect } from 'react';
import type { FEPPContentDonation } from '../types';
import DonationItems from './DonationItems';
import styles from './DonationList.module.css';
import useDonationListData from './useDonationListData';

// when removing this component also remove the design system component DonationListItem

const ITEMS_PER_PAGE = 8;
export type DonationListProps = {
  fundraisingEventId: string;
  donationFormUrl: string;
  title: string;
  donations: FEPPContentDonation[];
  receivedDonationsCount: number;
  shouldUseDonationFeed: boolean | undefined;
};
function Buttons(t0) {
  const $ = _c(26);
  const {
    receivedDonationsCount,
    hasNewerDonations,
    getNewerDonations,
    getOlderDonations,
    shouldGetOlderDonations,
    shouldGetNewerDonations,
    loading,
    id,
    title
  } = t0;
  const t = useTranslations("nextjs.receiver_pages.fundraising_event");
  let t1;
  if ($[0] !== id || $[1] !== title) {
    t1 = paginationDirection => {
      analytics.donationPaginationClicked({
        id,
        page_type: "fundraising_event",
        title,
        pagination_direction: paginationDirection
      });
    };
    $[0] = id;
    $[1] = title;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const trackDonationPaginationClicked = t1;
  let t2;
  if ($[3] !== getNewerDonations || $[4] !== trackDonationPaginationClicked) {
    t2 = () => {
      trackDonationPaginationClicked("back");
      getNewerDonations();
    };
    $[3] = getNewerDonations;
    $[4] = trackDonationPaginationClicked;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  const onClickBack = t2;
  let t3;
  if ($[6] !== getOlderDonations || $[7] !== trackDonationPaginationClicked) {
    t3 = () => {
      trackDonationPaginationClicked("forward");
      getOlderDonations();
    };
    $[6] = getOlderDonations;
    $[7] = trackDonationPaginationClicked;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  const onClickForward = t3;
  if (!receivedDonationsCount || receivedDonationsCount < ITEMS_PER_PAGE) {
    return null;
  }
  let t4;
  if ($[9] !== hasNewerDonations || $[10] !== loading || $[11] !== onClickBack || $[12] !== shouldGetNewerDonations || $[13] !== t) {
    t4 = hasNewerDonations && <IconButton className={styles.pageButton} kind="secondary" ghost={true} iconName="chevronLeft" onClick={onClickBack} disabled={!shouldGetNewerDonations || loading} iconProps={{
      title: t("previous_page_with_donations")
    }} />;
    $[9] = hasNewerDonations;
    $[10] = loading;
    $[11] = onClickBack;
    $[12] = shouldGetNewerDonations;
    $[13] = t;
    $[14] = t4;
  } else {
    t4 = $[14];
  }
  const t5 = !shouldGetOlderDonations || loading;
  let t6;
  if ($[15] !== t) {
    t6 = t("next_page_with_donations");
    $[15] = t;
    $[16] = t6;
  } else {
    t6 = $[16];
  }
  let t7;
  if ($[17] !== t6) {
    t7 = {
      title: t6
    };
    $[17] = t6;
    $[18] = t7;
  } else {
    t7 = $[18];
  }
  let t8;
  if ($[19] !== onClickForward || $[20] !== t5 || $[21] !== t7) {
    t8 = <IconButton kind="secondary" ghost={true} iconName="chevronRight" className={styles.pageButton} onClick={onClickForward} disabled={t5} iconProps={t7} />;
    $[19] = onClickForward;
    $[20] = t5;
    $[21] = t7;
    $[22] = t8;
  } else {
    t8 = $[22];
  }
  let t9;
  if ($[23] !== t4 || $[24] !== t8) {
    t9 = <LayoutCluster className={styles.buttonContainer} space="50">{t4}{t8}</LayoutCluster>;
    $[23] = t4;
    $[24] = t8;
    $[25] = t9;
  } else {
    t9 = $[25];
  }
  return t9;
}
function DonationList(t0) {
  const $ = _c(33);
  const {
    fundraisingEventId,
    donationFormUrl,
    title,
    donations,
    receivedDonationsCount: receivedDonationsCount_,
    onDonationCountChange
  } = t0;
  const t = useTranslations("nextjs.donation_list");
  const locale = useLocale();
  const {
    receivedDonationsCount: receivedDonationsCount__,
    donationsToDisplay: donationsToDisplay_,
    hasNewerDonations,
    getOlderDonations,
    getNewerDonations,
    shouldGetOlderDonations,
    shouldGetNewerDonations,
    loading
  } = useDonationListData(fundraisingEventId, ITEMS_PER_PAGE);
  const nonNullableReceivedDonationsCount = receivedDonationsCount__ ?? 0;
  const receivedDonationsCount = Math.max(nonNullableReceivedDonationsCount, receivedDonationsCount_);
  const space = typeof receivedDonationsCount === "number" && receivedDonationsCount < ITEMS_PER_PAGE ? "400" : "200";
  let t1;
  if ($[0] !== donations || $[1] !== donationsToDisplay_) {
    t1 = donationsToDisplay_.length ? donationsToDisplay_ : donations.slice(0, 8);
    $[0] = donations;
    $[1] = donationsToDisplay_;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const donationsToDisplay = t1;
  let t2;
  let t3;
  if ($[3] !== onDonationCountChange || $[4] !== receivedDonationsCount) {
    t2 = () => {
      onDonationCountChange(receivedDonationsCount);
    };
    t3 = [receivedDonationsCount, onDonationCountChange];
    $[3] = onDonationCountChange;
    $[4] = receivedDonationsCount;
    $[5] = t2;
    $[6] = t3;
  } else {
    t2 = $[5];
    t3 = $[6];
  }
  useEffect(t2, t3);
  let t4;
  if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = <Icon className={styles.headingIcon} name="logo" size="500" />;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  let t5;
  if ($[8] !== locale || $[9] !== receivedDonationsCount || $[10] !== t) {
    t5 = t("heading", {
      count: receivedDonationsCount.toLocaleString(locale)
    });
    $[8] = locale;
    $[9] = receivedDonationsCount;
    $[10] = t;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  let t6;
  if ($[12] !== t5) {
    t6 = <Heading className={styles.heading} level="heading-sm" as="h2">{t4}<span>{t5}</span></Heading>;
    $[12] = t5;
    $[13] = t6;
  } else {
    t6 = $[13];
  }
  let t7;
  if ($[14] !== fundraisingEventId || $[15] !== getNewerDonations || $[16] !== getOlderDonations || $[17] !== hasNewerDonations || $[18] !== loading || $[19] !== receivedDonationsCount || $[20] !== shouldGetNewerDonations || $[21] !== shouldGetOlderDonations || $[22] !== title) {
    t7 = <Buttons receivedDonationsCount={receivedDonationsCount} hasNewerDonations={hasNewerDonations} getNewerDonations={getNewerDonations} getOlderDonations={getOlderDonations} shouldGetOlderDonations={shouldGetOlderDonations} shouldGetNewerDonations={shouldGetNewerDonations} loading={loading} id={fundraisingEventId} title={title} />;
    $[14] = fundraisingEventId;
    $[15] = getNewerDonations;
    $[16] = getOlderDonations;
    $[17] = hasNewerDonations;
    $[18] = loading;
    $[19] = receivedDonationsCount;
    $[20] = shouldGetNewerDonations;
    $[21] = shouldGetOlderDonations;
    $[22] = title;
    $[23] = t7;
  } else {
    t7 = $[23];
  }
  let t8;
  if ($[24] !== donationFormUrl || $[25] !== donationsToDisplay || $[26] !== receivedDonationsCount) {
    t8 = <DonationItems receivedDonationsCount={receivedDonationsCount} donationsToDisplay={donationsToDisplay} itemsPerPage={ITEMS_PER_PAGE} donationFormUrl={donationFormUrl} />;
    $[24] = donationFormUrl;
    $[25] = donationsToDisplay;
    $[26] = receivedDonationsCount;
    $[27] = t8;
  } else {
    t8 = $[27];
  }
  let t9;
  if ($[28] !== space || $[29] !== t6 || $[30] !== t7 || $[31] !== t8) {
    t9 = <WaveyWrapper><LayoutStack space={space} className={styles.innerContainer}>{t6}{t7}{t8}</LayoutStack></WaveyWrapper>;
    $[28] = space;
    $[29] = t6;
    $[30] = t7;
    $[31] = t8;
    $[32] = t9;
  } else {
    t9 = $[32];
  }
  return t9;
}
export default DonationList;